// src/pages/OrgProfile.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccountType, getDaysRemaining, getOrgId, isOrgInactive } from '../context/accountUtils';
import api from '../services/api';
import { showToast } from '../context/ToastHandle';

// --- orgApi helpers using your axios instance ---
const orgApi = {
  getOrganisation: (orgId) => api.get('/organisations.php', { params: { org_id: orgId } }),
  updateOrganisation: ({ orgId, name, seat_limit }) =>
    api.put('/organisations.php', { org_id: orgId, name, seat_limit }),
  listMembers: (orgId) => api.get('/org_members.php', { params: { org_id: orgId } }),
  createMember: ({ orgId, name, email }) =>
    api.post('/org_members.php', { org_id: orgId, name, email }),
  patchMember: ({ orgId, userId, role, email, reset_password }) =>
    api.patch('/org_members.php', {
      org_id: orgId,
      user_id: userId,
      ...(role ? { role } : {}),
      ...(email ? { email } : {}),
      ...(reset_password ? { reset_password: true } : {}),
    }),
  deleteMember: ({ orgId, userId }) =>
    api.delete('/org_members.php', { params: { org_id: orgId, user_id: userId } }),
  addExisting: ({ orgId, existingEmail }) =>
    api.post('/org_members.php', { 
      org_id: orgId, 
      existing_email: existingEmail.trim() 
    }),
};

// --- shared styles to match UserProfile.jsx ---
const sectionStyle = {
  border: '1px solid #ccc',
  borderRadius: '6px',
  padding: '1rem',
  marginBottom: '1rem',
  backgroundColor: '#fff',
};

const inputStyle = {
  display: 'block',
  marginBottom: '0.5rem',
  padding: '0.5rem',
  width: '90%',
  border: '1px solid #ccc',
  borderRadius: '4px',
};

const buttonStyle = (bgColor, color = '#fff') => ({
  padding: '0.4rem 1rem',
  marginTop: '0.5rem',
  backgroundColor: bgColor,
  color,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
});

// --- component ---
export default function OrgProfile() {
  const orgId = getOrgId();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // inline edit org name
  const [editingName, setEditingName] = useState(false);
  const [orgNameDraft, setOrgNameDraft] = useState('');

  // summary fields
  const inactive = isOrgInactive();
  const plan = getAccountType();
  const timeLeft = getDaysRemaining('string');

  // add existing + create new
  const [existingEmail, setExistingEmail] = useState('');
  const [newMember, setNewMember] = useState({ name: '', email: '' });

  // per-row edit state
  const [editRow, setEditRow] = useState(null); // member id
  const [rowDraft, setRowDraft] = useState({ email: '', role: '' });

  const usage = useMemo(
    () => (org ? (org.seat_usage ?? members.length) : members.length),
    [org, members]
  );

  async function refresh() {
    if (!orgId) return;
    setLoading(true);
    setError('');
    try {
      const orgRes = await orgApi.getOrganisation(orgId);
      const o = orgRes?.data || orgRes;
      setOrg(o);
      setOrgNameDraft(o?.name || '');
      const memRes = await orgApi.listMembers(orgId);
      const list = memRes?.data?.members || memRes?.members || [];
      setMembers(list);
    } catch (e) {
      console.error(e);
      setError('Failed to load organisation.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!orgId) return;
    refresh();
  }, [orgId]);

  // Admin list
  const owners = members.filter((m) => m.role === 'owner');
  const admins = members.filter((m) => m.role === 'admin');

  if (!orgId) {
    return (
      <div style={{ padding: '1rem', maxWidth: 800, margin: '0 auto' }}>
        <h2>Organisation</h2>
        <p>No organisation found for this account.</p>
        <button style={buttonStyle('#007bff')} onClick={() => nav('/boards')}>
          Back to Boards
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: 800, margin: '0 auto' }}>
      <h2>Organisation</h2>

      {inactive && (
        <div style={{
          background: '#fff7d6',
          color: '#6a5200',
          padding: '10px 12px',
          borderRadius: 6,
          marginBottom: '12px',
          border: '1px solid #f1e3a6',
        }}>
          Your organisation subscription is inactive.
        </div>
      )}

      {error && (
        <div style={{
          background: '#ffeaea',
          color: '#8a1f1f',
          padding: '10px 12px',
          borderRadius: 6,
          marginBottom: '12px',
          border: '1px solid #f3c4c4',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          {/* Summary */}
          <div style={sectionStyle}>
            <div>
              <strong>Name:&nbsp;</strong>
              {!editingName ? (
                <span
                  style={{ textDecoration: 'underline dotted', cursor: 'pointer' }}
                  onClick={() => setEditingName(true)}
                >
                  {orgNameDraft || org?.name || '—'}
                </span>
              ) : (
                <span style={{ display: 'inline-flex', gap: 8 }}>
                  <input
                    value={orgNameDraft}
                    onChange={(e) => setOrgNameDraft(e.target.value)}
                    style={{ ...inputStyle, width: 260, marginBottom: 0 }}
                  />
                  <button
                    style={buttonStyle('#28a745')}
                    disabled={saving}
                    onClick={async () => {
                      try {
                        setSaving(true);
                        await orgApi.updateOrganisation({
                          orgId,
                          name: orgNameDraft.trim() || org?.name || '',
                          seat_limit: org?.seat_limit ?? usage,
                        });
                        setEditingName(false);
                        await refresh();
                      } catch (e) {
                        showToast('Failed to update org name.', 'error', false);
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    Save
                  </button>
                  <button
                    style={buttonStyle('#6c757d')}
                    onClick={() => {
                      setOrgNameDraft(org?.name || '');
                      setEditingName(false);
                    }}
                  >
                    Cancel
                  </button>
                </span>
              )}
            </div>
            <div><strong>Owner:</strong> {owners.map((m) => m.email).join(', ') || 'No owner'}</div>
            <div><strong>Admin:</strong> {admins.map((m) => m.email).join(', ') || 'No admin'}</div>
            <div><strong>Org Account:</strong> {org?.acc_type || plan}</div>
            <div><strong>Time left:</strong> {timeLeft}</div>
            <div><strong>Members:</strong> {members.length}</div>
          </div>

          {/* Members */}
          <div style={sectionStyle}>
            <h3>Members</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '0.7fr 1.6fr 0.7fr 2fr',
              fontWeight: 600,
              paddingBottom: 8,
              borderBottom: '2px solid #ddd',
            }}>
              <div>Name</div><div>Email</div><div>Role</div><div>Actions</div>
            </div>

            {members.map((m) => {
              const editing = editRow === m.id;
              return (
                <div key={m.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '0.7fr 1.6fr 0.7fr 2fr',
                  gap: 10, alignItems: 'center',
                  padding: '10px 0', borderBottom: '1px solid #eee',
                }}>
                  <div>{m.name || '—'}</div>
                  <div>
                    {!editing ? m.email : (
                      <input
                        style={{ ...inputStyle, marginBottom: 0 }}
                        value={rowDraft.email}
                        onChange={(e) => setRowDraft(d => ({ ...d, email: e.target.value }))}
                      />
                    )}
                  </div>
                  <div>
                    {!editing ? m.role : (
                      <select
                        style={{ ...inputStyle, marginBottom: 0 }}
                        value={rowDraft.role}
                        onChange={(e) => setRowDraft(d => ({ ...d, role: e.target.value }))}
                      >
                        <option value="owner">owner</option>
                        <option value="admin">admin</option>
                        <option value="member">member</option>
                      </select>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {!editing ? (
                      <>
                        <button style={buttonStyle('#6c757d')} onClick={() => {
                          setEditRow(m.id);
                          setRowDraft({ email: m.email, role: m.role });
                        }}>Edit</button>
                        <button
                          style={buttonStyle('#ffc107', '#000')}
                          onClick={async () => {
                            if (!window.confirm('Remove this member?')) return;
                            await orgApi.deleteMember({ orgId, userId: m.id });
                            await refresh();
                          }}
                          disabled={m.role === 'owner'}
                        >Remove</button>
                      </>
                    ) : (
                      <>
                        <button style={buttonStyle('#28a745')} onClick={async () => {
                          try {
                            if (rowDraft.role && rowDraft.role !== m.role) {
                              if (rowDraft.role === 'owner') {
                                if (!window.confirm('Transfer ownership to this user? You will become an admin.')) return;
                                await api.patch('/org_members.php', {
                                  org_id: orgId,
                                  user_id: m.id,
                                  action: 'transfer_owner',
                                });
                              } else {
                                await orgApi.patchMember({ orgId, userId: m.id, role: rowDraft.role });
                              }
                            }
                            if (rowDraft.email && rowDraft.email !== m.email) {
                              await orgApi.patchMember({ orgId, userId: m.id, email: rowDraft.email });
                            }
                            setEditRow(null);
                            await refresh();
                          } catch (e) {
                            showToast('Failed to save changes.', 'error', false);
                          }
                        }}>Save</button>
                        <button style={buttonStyle('#6c757d')} onClick={() => {
                          setEditRow(null); setRowDraft({ email: '', role: '' });
                        }}>Cancel</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add existing */}
          <div style={sectionStyle}>
            <h3>Add Existing Member</h3>
            <input
              style={inputStyle}
              type="email"
              placeholder="Existing user email"
              value={existingEmail}
              onChange={(e) => setExistingEmail(e.target.value)}
            />
            <button
              style={buttonStyle('#1976d2')}
              onClick={async () => {
                if (!existingEmail.trim()) return showToast('Email required', 'error', false);
                await orgApi.addExisting({ orgId, existingEmail });
                setExistingEmail('');
                await refresh();
              }}
            >Add</button>
          </div>

          {/* Create new */}
          <div style={sectionStyle}>
            <h3>Create New Member</h3>
            <input
              style={inputStyle}
              placeholder="Name"
              value={newMember.name}
              onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
            />
            <input
              style={inputStyle}
              type="email"
              placeholder="Email"
              value={newMember.email}
              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
            />
            <button
              style={buttonStyle('#28a745')}
              onClick={async () => {
                if (!newMember.email.trim()) return showToast('Email required', 'error', false);
                await orgApi.createMember({ orgId, name: newMember.name.trim(), email: newMember.email.trim() });
                setNewMember({ name: '', email: '' });
                await refresh();
              }}
            >Create & Add</button>
          </div>
        </>
      )}
    </div>
  );
}
